import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import type { SubscriptionStatus, SubscriptionTier } from "@livingsyncs/types";
import api from "../lib/apiClient";

export interface SubscriptionDetails {
  id: string | null;
  status: SubscriptionStatus;
  /**
   * US-138: Stripe's `cancel_at_period_end` — the subscription is live and
   * still reports `status: "active"`, but will not renew.
   *
   * Derive the "Canceling" badge and the Reactivate button from THIS, never
   * from `status === "canceled"`. Stripe keeps a pending-cancellation
   * subscription at `active` until the period actually ends, so the old
   * status-based approach was overwritten by the very next webhook and the
   * cancellation appeared to silently undo itself.
   */
  cancelAtPeriodEnd: boolean;
  tier: SubscriptionTier | null;
  /** The tier prior to the most recent tier change; null if never changed. */
  previousTier: SubscriptionTier | null;
  facilityCount: number;
  /** Billing quantity actually charged by Stripe — usually equals
   * facilityCount, but a facility with >12 residents contributes more than
   * 1 unit (billed per block of 8 residents at the same per-unit rate). */
  billableUnits: number;
  /**
   * Plan cost BEFORE tax (unit price × billableUnits), computed locally.
   *
   * This is not what the customer pays. Stripe Managed Payments adds tax as
   * merchant of record — a real purchase quoted $2,000.00 from this field and
   * charged $2,300.00. Show `billing.total` when it is present and treat this
   * as the pre-tax line; only fall back to it when `billing` is null.
   */
  monthlyAmount: number;
  /**
   * The real next-invoice figures read back from Stripe. Null before a
   * subscription exists (no invoice to price, no address to tax against) or if
   * Stripe was unreachable — callers must handle null rather than assume a total.
   */
  billing: {
    subtotal: number;
    tax: number;
    total: number;
    currency: string;
  } | null;
  currentPeriodEnd: string | null;
  trialEndsAt: string | null;
  trialDaysRemaining: number;
  stripeCustomerId: string | null;
  pricing: {
    standard: {
      pricePerFacility: number;
      totalMonthly: number;
    };
    premium: {
      pricePerFacility: number;
      totalMonthly: number;
    };
  };
}

/**
 * Get current subscription details
 * @param enabled - Whether to enable the query (default: true). Set to false to prevent API call.
 * @param refetchIntervalMs - Optional short-lived poll interval. Used right
 *   after a Stripe Checkout redirect (US-118): there's a real propagation gap
 *   between the browser landing back on this page and the
 *   `checkout.session.completed` webhook actually updating the DB, so the
 *   caller polls briefly instead of assuming the first read is current.
 *   Polling stops automatically once `data.id` is populated (the webhook
 *   landed) even if the caller keeps passing an interval.
 */
export function useSubscription(enabled = true, refetchIntervalMs?: number) {
  return useQuery<SubscriptionDetails | null>({
    queryKey: ["subscription"],
    queryFn: async () => {
      const { data } = await api.get("/subscriptions/current");
      return data.data;
    },
    staleTime: 30 * 1000, // 30 seconds
    enabled,
    refetchInterval: refetchIntervalMs
      ? (query) => (query.state.data?.id ? false : refetchIntervalMs)
      : undefined,
  });
}

/**
 * Start a new subscription. US-118: this creates a Stripe Checkout Session
 * server-side and returns its URL — the caller is responsible for
 * redirecting the browser there (`window.location.href = checkoutUrl`).
 * There is no local "subscribed" state to invalidate here: the subscription
 * isn't created until the user completes Checkout and Stripe's
 * `checkout.session.completed` webhook writes the real status.
 */
export function useCreateSubscription() {
  return useMutation({
    mutationFn: async (tier: SubscriptionTier) => {
      const { data } = await api.post("/subscriptions/create", { tier });
      return data.data as { checkoutUrl: string };
    },
  });
}

/**
 * Upgrade/downgrade subscription tier
 */
export function useUpgradeSubscription() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (tier: SubscriptionTier) => {
      const { data } = await api.put("/subscriptions/upgrade", { tier });
      return data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["subscription"] });
    },
  });
}

/**
 * Cancel subscription
 */
export function useCancelSubscription() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const { data } = await api.post("/subscriptions/cancel");
      return data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["subscription"] });
    },
  });
}

/**
 * Reactivate canceled subscription
 */
export function useReactivateSubscription() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const { data } = await api.post("/subscriptions/reactivate");
      return data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["subscription"] });
    },
  });
}

/**
 * US-138: open the Stripe-hosted Billing Portal so the customer can replace a
 * failing or expired card.
 *
 * There was previously no payment-method update path anywhere in the product —
 * three separate surfaces told the customer to "update your payment method"
 * with nothing to click, which made `past_due` unrecoverable without a support
 * ticket.
 *
 * Like Checkout (`useCreateSubscription`), the caller redirects the browser to
 * the returned URL. Nothing local to invalidate: any resulting change arrives
 * via Stripe webhook, not from this response.
 */
export function useBillingPortal() {
  return useMutation({
    mutationFn: async () => {
      const { data } = await api.post("/subscriptions/portal");
      return data.data as { portalUrl: string };
    },
  });
}
